import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { buscarDashboard } from "./api";
import type { DashboardOut } from "./tipos";

/*
 * Hook de server-state do dashboard (Tela 1 — landing em `/`). Toda leitura de
 * servidor passa pelo TanStack Query: cache, dedup e revalidação com a config
 * conservadora do `criarQueryClient`.
 *
 * MODELO DE ESCOPO: `empresaId` é `string | null`, onde `null` = visão GRUPO
 * (consolidado). Mas o fetch só deve disparar quando o usuário JÁ ESCOLHEU um
 * escopo — antes disso a página não chama o hook (passa `enabled: false` via o
 * próprio gate do componente). Para distinguir "grupo escolhido" (null, deve
 * disparar) de "nada escolhido" (não deve disparar), o caller informa
 * `habilitado`: a página só o liga depois que o `SeletorEscopo` resolveu.
 *
 * A queryKey inclui `empresaId` e `horizonte`: trocar qualquer um é uma query
 * distinta no cache (grupo vs. empresa vs. horizonte diferente).
 */

/**
 * Dashboard de um escopo (empresa `empresaId` ou GRUPO quando `empresaId` é
 * null). Só dispara quando `habilitado` é true — o componente liga apenas depois
 * que o usuário escolheu um escopo (e, para grupo, apenas se for irrestrito; o
 * `SeletorEscopo` já restringe a opção). A queryKey inclui empresaId+horizonte.
 */
export function useDashboard(
  empresaId: string | null,
  horizonte: number,
  habilitado: boolean,
): UseQueryResult<DashboardOut> {
  return useQuery({
    queryKey: ["dashboard", empresaId, horizonte],
    queryFn: () => buscarDashboard(empresaId, horizonte),
    enabled: habilitado,
  });
}
